/* import __COLOCATED_TEMPLATE__ from './team-configuration.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';

interface Arguments {
  onPrimaryTeamsChange: (primaryTeams: number[]) => void;
  onSecondaryTeamsChange: (secondaryTeams: number[]) => void;
}

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

const TEAM_SETTING = 'team-setting';
const LOAD_BALANCED = 'load_balanced';

export default class TeamConfiguration extends Component<Signature> {
  @service declare store: Store;

  @tracked selectedPrimary: number[] = [];
  @tracked selectedSecondary: number[] = [];
  @tracked showTeamConfiguration = false;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.fetchTeamSettings();
  }

  async fetchTeamSettings(): Promise<void> {
    await this.store.findAll(TEAM_SETTING);
  }

  get allSelectedTeams(): number[] {
    return [...this.selectedPrimary, ...this.selectedSecondary];
  }

  get excludedFromPrimaryTeamIds(): Set<number> {
    return new Set(this.allSelectedTeams);
  }

  get excludedFromSecondaryTeamIds(): Set<number> {
    let nonLBTeamIds = this.store
      .peekAll(TEAM_SETTING)
      .filter((teamSetting) => teamSetting.distributionMethod !== LOAD_BALANCED)
      .map((teamSetting) => parseInt(teamSetting.teamId, 10));

    return new Set([...this.allSelectedTeams, ...nonLBTeamIds]);
  }

  @action onPrimaryTeamsChange(primaryTeams: number[]): void {
    this.selectedPrimary = primaryTeams;

    this.args.onPrimaryTeamsChange(primaryTeams);
  }

  @action onSecondaryTeamsChange(secondaryTeams: number[]): void {
    this.selectedSecondary = secondaryTeams;

    this.args.onSecondaryTeamsChange(secondaryTeams);
  }

  @action toggleTeamConfiguration(): void {
    this.showTeamConfiguration = !this.showTeamConfiguration;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Invites::Configuration::TeamConfiguration': typeof TeamConfiguration;
    'settings/teammates/invites/configuration/team-configuration': typeof TeamConfiguration;
  }
}
